import React, { useEffect, useState } from 'react';
import { MousePointerClick, Eye, ExternalLink, CalendarCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GymPipelineCard() {
  const [data, setData] = useState({ gyms: [], cities: [], consults: [] });

  useEffect(() => {
    fetch(`${API_URL}/api/admin/gym-pipeline`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('admin_token')}` },
    })
      .then((r) => r.json())
      .then(setData)
      .catch(console.error);
  }, []);

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="gym-pipeline-card">
      <div className="flex items-center gap-2 mb-1">
        <MousePointerClick className="w-4 h-4 text-indigo-500" />
        <h2 className="text-sm font-bold text-gray-900">Gym Pipeline — UTM Click Tracking</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Outbound clicks on tracked gym links (utm_campaign contains <code className="bg-gray-100 px-1 rounded">hyrox_gym_directory</code>) — your traffic proof for client outreach.
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        {data.cities.map((c) => (
          <div key={c.city} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100" data-testid={`pipeline-views-${c.city}`}>
            <Eye className="w-3 h-3 text-gray-400" />
            <span className="text-xs font-semibold text-gray-700">{c.label}</span>
            <span className="text-xs font-bold text-indigo-600">{c.page_views_30d}</span>
            <span className="text-[10px] text-gray-400">views/30d</span>
          </div>
        ))}
      </div>

      <div className="mb-5 p-4 rounded-lg bg-lime-50/60 border border-lime-100" data-testid="consult-tracking-tile">
        <div className="flex items-center gap-2 mb-2">
          <CalendarCheck className="w-4 h-4 text-lime-600" />
          <h3 className="text-xs font-bold text-gray-900">Consultation Clicks — Calendly</h3>
        </div>
        {(data.consults || []).length === 0 ? (
          <p className="text-xs text-gray-500" data-testid="consult-tile-empty">
            No consultation clicks yet. Clicks on Calendly consult CTAs (e.g. Hunger in the Wild's "Book a Free Consultation") land here — completed bookings show <code className="bg-white px-1 rounded">utm_source=ninjathlete</code> inside Calendly.
          </p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {data.consults.map((c) => (
              <div key={c.utm_content || c.url} className="flex-1 min-w-[220px] bg-white rounded-lg border border-lime-100 p-3" data-testid={`consult-tile-${(c.utm_content || 'unknown').replace(/[^a-z0-9]+/gi, '-')}`}>
                <p className="text-xs font-bold text-gray-900 mb-1">{c.gym || c.utm_content}</p>
                <div className="flex items-end gap-4">
                  <div><span className="text-lg font-extrabold text-lime-600">{c.clicks_7d}</span><span className="text-[10px] text-gray-400 ml-1">7d</span></div>
                  <div><span className="text-lg font-extrabold text-gray-800">{c.clicks_30d}</span><span className="text-[10px] text-gray-400 ml-1">30d</span></div>
                  <div><span className="text-lg font-extrabold text-gray-500">{c.clicks_total}</span><span className="text-[10px] text-gray-400 ml-1">total</span></div>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Last click: {fmt(c.last_click)} · booked consults show utm_source=ninjathlete in Calendly</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {data.gyms.length === 0 ? (
        <p className="text-xs text-gray-400" data-testid="pipeline-empty">
          No tracked gym clicks yet. Clicks on UTM-tagged gym links (e.g. Arsenal Health's membership button) will appear here automatically.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="pipeline-clicks-table">
            <thead>
              <tr className="text-left text-gray-400 uppercase tracking-wide border-b border-gray-100">
                <th className="py-2 pr-3 font-semibold">Gym</th>
                <th className="py-2 pr-3 font-semibold">City</th>
                <th className="py-2 pr-3 font-semibold text-right">7d</th>
                <th className="py-2 pr-3 font-semibold text-right">30d</th>
                <th className="py-2 pr-3 font-semibold text-right">Total</th>
                <th className="py-2 pr-3 font-semibold">Last Click</th>
                <th className="py-2 font-semibold">Top Source</th>
              </tr>
            </thead>
            <tbody>
              {data.gyms.map((g) => (
                <tr key={g.utm_content || g.url} className="border-b border-gray-50" data-testid={`pipeline-row-${(g.utm_content || 'unknown').replace(/[^a-z0-9]+/gi, '-')}`}>
                  <td className="py-2.5 pr-3">
                    <span className="font-semibold text-gray-800">{g.gym || g.utm_content || '—'}</span>
                    {g.url && (
                      <a href={g.url} target="_blank" rel="noopener noreferrer" className="ml-1.5 inline-flex align-middle text-gray-300 hover:text-indigo-500">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-gray-600">{g.city || '—'}</td>
                  <td className="py-2.5 pr-3 text-right font-semibold text-gray-800">{g.clicks_7d}</td>
                  <td className="py-2.5 pr-3 text-right text-gray-600">{g.clicks_30d}</td>
                  <td className="py-2.5 pr-3 text-right font-bold text-indigo-600">{g.clicks_total}</td>
                  <td className="py-2.5 pr-3 text-gray-500">{fmt(g.last_click)}</td>
                  <td className="py-2.5 text-gray-500 truncate max-w-[180px]">{g.top_source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
